import { useMemo } from 'react';

import type {
  ChatAdapter,
  ChatMessage as MuiChatMessage,
} from '@mui/x-chat/core';

import type {
  ChatCardItem,
  ChatCardItemState,
  ChatCardTone,
  ChatMessage,
  MenuContent,
} from '@karehero/models';

export const noopChatAdapter: ChatAdapter = {
  async sendMessage() {
    return new ReadableStream({ start: (c) => c.close() });
  },
};

type MuiPart = MuiChatMessage['parts'][number];

// MUI X Chat's part union only covers built-in kinds; custom `data-*`
// parts go through `partRenderers` at runtime but aren't in the type.
const dataPart = (
  type: `data-${string}`,
  data: Record<string, unknown>,
): MuiPart => ({ type, data }) as MuiPart;

export interface VoicePartData {
  durationSeconds: number;
  documentId?: string;
  levels?: number[];
  transcript?: string;
}

export type MenuPartData = Omit<MenuContent, 'kind'>;

export interface CardPartData {
  tone: ChatCardTone;
  title: string;
  lines: string[];
  items?: ChatCardItem[];
  isEditable: boolean;
  isPinnable: boolean;
}

export interface FilePartData {
  name: string;
  contentType: string;
  sizeBytes: number;
  documentId?: string;
  isProcessed?: boolean;
}

const asRecord = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;

export const voicePartData = (part: {
  data?: unknown;
}): VoicePartData | null => {
  const d = asRecord(part.data);
  if (typeof d?.durationSeconds !== 'number') return null;
  return {
    durationSeconds: d.durationSeconds,
    documentId: typeof d.documentId === 'string' ? d.documentId : undefined,
    levels: Array.isArray(d.levels)
      ? d.levels.filter((n): n is number => typeof n === 'number')
      : undefined,
    transcript:
      typeof d.transcript === 'string' && d.transcript
        ? d.transcript
        : undefined,
  };
};

type MenuOption = MenuContent['options'][number];

const explanationOf = (value: unknown): MenuOption['explain'] | undefined => {
  const d = asRecord(value);
  if (typeof d?.title !== 'string' || !Array.isArray(d.points)) {
    return undefined;
  }
  return {
    title: d.title,
    points: d.points.filter((p): p is string => typeof p === 'string'),
    ...(d.isKept === true && { isKept: true }),
  };
};

/** A menu's options as saved, keeping only those with an id and a label. */
const optionsOf = (value: unknown): MenuOption[] =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        const d = asRecord(item);
        if (typeof d?.id !== 'string' || typeof d.label !== 'string') {
          return [];
        }
        const explain = explanationOf(d.explain);
        return [
          {
            id: d.id,
            label: d.label,
            ...(typeof d.isPrimary === 'boolean' && { isPrimary: d.isPrimary }),
            ...(d.isQuiet === true && { isQuiet: true }),
            ...(explain && { explain }),
          },
        ];
      })
    : [];

export const menuPartData = (part: { data?: unknown }): MenuPartData | null => {
  const d = asRecord(part.data);
  if (typeof d?.text !== 'string') return null;
  return {
    text: d.text,
    options: optionsOf(d.options),
    pickedId: typeof d.pickedId === 'string' ? d.pickedId : undefined,
    isChangeable: d.isChangeable === true,
    isClosed: d.isClosed === true,
  };
};

export const filePartData = (part: { data?: unknown }): FilePartData | null => {
  const d = asRecord(part.data);
  if (typeof d?.name !== 'string') return null;
  return {
    name: d.name,
    contentType: typeof d.contentType === 'string' ? d.contentType : '',
    sizeBytes: typeof d.sizeBytes === 'number' ? d.sizeBytes : 0,
    documentId: typeof d.documentId === 'string' ? d.documentId : undefined,
    isProcessed: d.isProcessed === true,
  };
};

const CARD_TONES: ChatCardTone[] = [
  'prompts',
  'heard',
  'help',
  'need',
  'reading',
  'collected',
];

const ITEM_STATES: ChatCardItemState[] = ['done', 'doing', 'waiting'];

const itemsOf = (value: unknown): ChatCardItem[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  return value.flatMap((item) => {
    const d = asRecord(item);
    const state = ITEM_STATES.find((s) => s === d?.state);
    if (typeof d?.label !== 'string' || !state) return [];
    return [
      {
        label: d.label,
        state,
        ...(typeof d.found === 'string' && { found: d.found }),
      },
    ];
  });
};

export const cardPartData = (part: { data?: unknown }): CardPartData | null => {
  const d = asRecord(part.data);
  if (typeof d?.title !== 'string' || !Array.isArray(d.lines)) return null;
  return {
    tone: CARD_TONES.find((tone) => tone === d.tone) ?? 'help',
    title: d.title,
    lines: d.lines.filter((line): line is string => typeof line === 'string'),
    items: itemsOf(d.items),
    isEditable: d.isEditable === true,
    isPinnable: d.isPinnable === true,
  };
};

const toParts = (content: ChatMessage['content']): MuiPart[] => {
  switch (content.kind) {
    case 'text':
    case 'answers':
      return [{ type: 'text', text: content.text }];
    case 'voice':
      return [
        dataPart('data-voice', {
          durationSeconds: content.durationSeconds,
          documentId: content.documentId,
          levels: content.levels,
          transcript: content.transcript,
        }),
      ];
    case 'file':
      return [
        dataPart('data-file', {
          name: content.name,
          contentType: content.contentType,
          sizeBytes: content.sizeBytes,
          documentId: content.documentId,
          isProcessed: content.isProcessed,
        }),
      ];
    case 'card':
      return [
        dataPart('data-card', {
          tone: content.tone,
          title: content.title,
          lines: content.lines,
          items: content.items,
          isEditable: content.isEditable,
          isPinnable: content.isPinnable,
        }),
      ];
    case 'menu':
      return [
        dataPart('data-menu', {
          text: content.text,
          options: content.options,
          pickedId: content.pickedId,
          isChangeable: content.isChangeable,
          isClosed: content.isClosed,
        }),
      ];
  }
};

/** Stands where the next system message will land while it is on its way. */
export const TYPING_MESSAGE: MuiChatMessage = {
  id: 'typing',
  role: 'assistant',
  parts: [dataPart('data-typing', {})],
  status: 'sent',
};

/** Maps our chat messages onto the shape MUI X Chat renders. */
export const useMuiChatMessages = (
  messages: ChatMessage[],
  isTyping = false,
): MuiChatMessage[] =>
  useMemo(() => {
    const mapped: MuiChatMessage[] = messages.map((message) => ({
      id: message.id,
      role: message.from === 'system' ? 'assistant' : 'user',
      parts: toParts(message.content),
      status: message.status,
      metadata: message.isBeingRead ? { isBeingRead: true } : undefined,
    }));
    return isTyping ? [...mapped, TYPING_MESSAGE] : mapped;
  }, [isTyping, messages]);
